/**
 * The save — POST or PATCH, the whole tree, and every outcome named.
 *
 * One function, one call, three possible answers. That shape is the point: a caller that had to
 * catch an error, read its status, decide whether a 409 is special, refetch a tree and map a code to
 * a message would be re-implementing the save protocol at every call site, and the protocol is the
 * part that must not vary.
 *
 * ── POST or PATCH is decided by the draft, not by the caller ─────────────────────────────────
 * `id == null` is a plan that has never been stored. The two bodies share the same nested phase tree
 * and differ only at the top level (`draftToRequest`), so this is the only place that has to know
 * which verb a draft implies.
 *
 * **The POST branch is live.** `features/create-plan` seeds a local draft with `createEmptyDraft`
 * (`id: null`) and navigates to `/plans/nowy`; the builder's first Zapisz comes through here and
 * POSTs it. That is the whole of "no autosave" — nothing is written until the trainer asks for it.
 * The gap this comment used to record ("no surface produces a draft with a null id … there is no
 * create route") closed on 5 August 2026 and the note is corrected rather than left standing.
 *
 * ── `lang` goes on the WRITE, and there is no second read ────────────────────────────────────
 * Both `POST /plan-template` and `PATCH /plan-template/{id}` take a `lang` query parameter and both
 * return the full tree through it: `PlanTemplateProcessor.UpdateAsync` ends with
 * `return await GetByIdAsync(planTemplateId, language)`, which is the same canonical query the read
 * endpoint serves. So the write's own response IS the fresh, localised, re-versioned tree, and a
 * follow-up `GET …?lang=pl` would spend a round trip to receive it a second time. Verified in the
 * backend source rather than assumed, because the assumption in circulation — that the write paths
 * answer in English — is what would have justified that second read.
 *
 * Omitting `lang` is silently wrong rather than an error: a blank one resolves to English, so the
 * trainer would watch every exercise name switch language the moment they pressed Save.
 *
 * ── The structural gate refuses a save whose flow was never RUN ──────────────────────────────
 * `structuralSaveRoute` answers what a save owes the trainer; this is the one place that answer is
 * enforced. Both non-direct routes are live, and neither takes a round trip:
 *
 * · `warn` — a live fork delivered in person, where a structural edit moves or frees the TRAINER's
 *   own reservations.
 * · `confirm-dates` — a phase boundary moved, so a cycle changed phase and its occurrences land on
 *   the new phase's weekdays. Nothing measures that; the consent names the consequence in words.
 *
 * `structuralConsent` means "**I ran that flow and hold its answer**", not "skip the check". A
 * caller that did neither is refused, and the gate stays for exactly that reason: the consent must
 * be impossible to omit by forgetting. A save that silently re-dated a client's calendar would be
 * the worst possible way to discover a missing dialog.
 */

import { postAiConversationsConversationIdBind } from '~/shared/api/generated/endpoints/ai/ai';
import { getPlanTemplateIdFull, patchPlanTemplateId, postPlanTemplate } from '~/shared/api/generated/endpoints/plan-template/plan-template';
import type { TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse } from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';

import { DraftSerialisationError, draftToPatchRequest, draftToPostRequest } from '../model/draftToRequest';
import type { PlanDraft, PlanOrigin } from '../model/planDraft.types';
import { saveErrorCopyKey } from '../model/saveErrorCopy';
import { structuralSaveRoute } from '../model/structuralEditFlow';
import type { ContentLanguage } from '~/shared/i18n/contentLanguage';

type FullResponse = TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse;

/** The key shown when a structural edit reaches the save path before its consent dialog exists. */
const STRUCTURAL_CONSENT_UNAVAILABLE_KEY = 'web:planBuilder.saveError.structuralConsentUnavailable';

/**
 * What a save attempt resolved to.
 *
 * `conflict` carries the WINNING tree rather than a boolean, because the only useful thing to do
 * with a lost race is show the trainer what they would be adopting — and refetching it is this
 * module's job, not the caller's. The two outcomes the trainer then chooses between (adopt, or keep
 * editing) are both expressible from this value, and no third one is: there is deliberately no shape
 * here that means "send mine anyway".
 */
export type SavePlanOutcome =
  | { readonly outcome: 'saved'; readonly response: FullResponse }
  | { readonly outcome: 'conflict'; readonly winning: FullResponse }
  | {
      readonly outcome: 'refused';
      /** A `web:` i18n key, already resolved against the plan's scheduling mode. */
      readonly messageKey: string;
      /**
       * The backend's own refusal code, verbatim, or absent where there was none — a serialisation
       * refusal this app raised itself, or one of the many 400s that carry no code.
       *
       * `messageKey` is the general answer and it stays the default. This is here for the callers
       * that can say something the general copy cannot: the calendar knows WHICH week its gesture
       * was about, and the backend's `plan_window_locked` names that week in English prose and in no
       * field. So the caller matches on the CODE — never on the message, which the server may
       * reword — and words the week itself. Nothing is obliged to look at it.
       */
      readonly code?: string;
      /**
       * What `messageKey` interpolates, and a NESTED key it interpolates as `{{reason}}`.
       *
       * Both absent for every refusal the backend words, which are one flat sentence each. A rule
       * refusal is two facts — which exercise, and what is wrong with its rule — and the second is
       * already written, in the rule bar's own copy. Composing them here rather than adding a second
       * full sentence per refusal is what keeps the bar and the save banner from drifting apart: one
       * of them would be reworded and the other would not.
       */
      readonly messageValues?: Readonly<Record<string, string | number>>;
      readonly reason?: {
        readonly key: string;
        readonly values: Readonly<Record<string, string | number>>;
      };
    };

export interface SavePlanArgs {
  readonly draft: PlanDraft;
  /** The interface language. Sent on the write so the response comes back in it. */
  readonly lang: ContentLanguage;
  /**
   * **The structural flow was run to completion, and this call carries its answer.**
   *
   * Not "the trainer agreed" and not "skip the gate" — it is the caller asserting that it resolved
   * `structuralSaveRoute`, put `structuralSaveStep`'s answer to the trainer if that asked for
   * consent, and is now committing what came back.
   *
   * It was previously documented as satisfying the `warn` route only, which is why it read as a
   * consent flag. Both routes accept it, and the gate below is one rule rather than two.
   */
  readonly structuralConsent?: boolean;
}

/** A refused save's code, when the backend sent one. Many 400s carry none — that is handled. */
function refusalCode(error: unknown): string | undefined {
  return error instanceof ApiError ? error.code : undefined;
}

/** The copy each serialisation refusal resolves to. The node's name is the one interpolation. */
const SERIALISATION_ERROR_KEYS: Readonly<Record<DraftSerialisationError['reason'], string>> = {
  emptyPhase: 'web:planBuilder.saveError.emptyPhase',
  unidentifiedExercise: 'web:planBuilder.saveError.unidentifiedExercise',
  ruleRefused: 'web:planBuilder.saveError.ruleRefused',
};

/**
 * A draft this app refused to serialise → words the trainer can act on.
 *
 * These never reached the trainer before: `draftToRequest` threw, nothing here caught it, and the
 * mutation's `onError` — whose whole remit is "a transport fault is not something the backend said"
 * — turned all three into "check your connection". The three refusals ARE something this app said,
 * about a node it can name, so they are outcomes rather than exceptions.
 *
 * A rule refusal additionally carries the rule bar's own sentence, so the banner explains the rule
 * in the same words the bar would.
 */
function serialisationRefusal(error: DraftSerialisationError): SavePlanOutcome {
  const key = SERIALISATION_ERROR_KEYS[error.reason];
  return error.refusal === null
    ? { outcome: 'refused', messageKey: key, messageValues: { name: error.nodeName } }
    : {
        outcome: 'refused',
        messageKey: key,
        messageValues: { name: error.nodeName },
        reason: error.refusal,
      };
}

/**
 * Was this a lost optimistic-concurrency race?
 *
 * Both halves are checked, and the code is the one that decides. The backend answers 409 with
 * `concurrency_conflict` (`EnsureConcurrencyVersion`, which runs BEFORE any mutation, so a lost race
 * is never a partial write). Treating every 409 as a conflict would fold in any other 409 the API
 * grows later and refetch a tree in answer to it.
 */
function isConcurrencyConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && error.code === 'concurrency_conflict';
}

/**
 * Tie Buddy's conversation to the template its draft just became (ai-plan-builder R13), so opening
 * the saved plan finds the same conversation. Called after the write that created the template (the
 * builder's first Zapisz here, an assignment in `features/assign-plan`) and never awaited by it: the
 * plan is stored whatever the bind answers, and a bind that fails leaves the conversation where it
 * was, under the draft key it was opened with, which is nothing the trainer has to act on. Answers
 * whether the bind landed, for the caller that has to let the draft key go itself.
 */
export function bindAiDraft(origin: PlanOrigin, planTemplateId: number | null | undefined): Promise<boolean> {
  if (origin.kind !== 'aiDraft' || planTemplateId === null || planTemplateId === undefined || planTemplateId <= 0) {
    return Promise.resolve(false);
  }
  // Any answer the server gave is a bind: linked to a library template, or only recorded on a
  // client's own copy (`planTemplateId: null` in the answer). Either way the draft is done.
  return postAiConversationsConversationIdBind(origin.conversationId, { planTemplateId }).then(
    () => true,
    () => false,
  );
}

/**
 * Send the whole tree.
 *
 * Never throws for a failure it can name: every modelled outcome comes back as a value, so the
 * caller has no error handling to invent and cannot accidentally treat a 409 as an ordinary failure.
 * A thrown non-`ApiError` — an aborted request, a transport fault — still propagates, because that
 * is not a save outcome and swallowing it would report a network drop as a backend refusal.
 */
export async function savePlanTemplate(args: SavePlanArgs): Promise<SavePlanOutcome> {
  const { draft, lang } = args;

  // ONE rule for both non-direct routes: this save owes the trainer an answer about their own
  // calendar or their own bookings, and the caller must have obtained it. Which of the two it was is
  // `structuralSaveStep`'s decision, made before this call and carried in on `structuralConsent`.
  const route = structuralSaveRoute(draft);
  if (route.route !== 'save-direct' && args.structuralConsent !== true) {
    return { outcome: 'refused', messageKey: STRUCTURAL_CONSENT_UNAVAILABLE_KEY };
  }

  try {
    const response =
      draft.id === null
        ? await postPlanTemplate(draftToPostRequest(draft), { lang })
        : await patchPlanTemplateId(draft.id, draftToPatchRequest(draft), { lang });
    if (draft.id === null) void bindAiDraft(draft.origin, response.id);
    return { outcome: 'saved', response };
  } catch (error) {
    // A draft this app refused to serialise, before any request left. It is a refusal like any
    // other from the caller's side — the difference is that we can say more about it than the
    // backend could, because we still have the tree and the node's name.
    if (error instanceof DraftSerialisationError) return serialisationRefusal(error);
    if (!(error instanceof ApiError)) throw error;

    if (isConcurrencyConflict(error) && draft.id !== null) {
      try {
        const winning = await getPlanTemplateIdFull(draft.id, { lang });
        return { outcome: 'conflict', winning };
      } catch {
        // The race was lost AND the winning tree cannot be read. There is nothing to diff and
        // nothing to offer, so the trainer gets words rather than a dialog with two dead buttons.
        return { outcome: 'refused', messageKey: saveErrorCopyKey('concurrency_conflict', draft.mode) };
      }
    }

    const code = refusalCode(error);
    return { outcome: 'refused', messageKey: saveErrorCopyKey(code, draft.mode), ...(code === undefined ? {} : { code }) };
  }
}
